import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import {
  CANAL_LABEL,
  ESTADO_LABEL,
  PERIODO_LABEL,
  formatDateTime,
  formatRelative,
} from '@/lib/format'

// ---- tipos ----
type Estado = 'pendiente' | 'en_proceso' | 'enviado' | 'cancelado'
type Canal = 'email' | 'whatsapp'
type Periodo = 'month' | '3months' | 'year'

type StatementRow = {
  id: string
  cuenta_id: string
  asociado_id: string
  canal: Canal
  contacto: string
  periodo: Periodo
  estado: Estado
  requested_at: string
  attended_at: string | null
  notas_admin: string | null
  // joined
  asociado?: {
    no_asociado: string
    nombre_completo: string
  } | null
}

const FILTERS: { id: Estado | 'all'; label: string }[] = [
  { id: 'pendiente', label: 'Pendientes' },
  { id: 'en_proceso', label: 'En proceso' },
  { id: 'enviado', label: 'Enviados' },
  { id: 'all', label: 'Todas' },
]

export function SolicitudesPage() {
  const [rows, setRows] = useState<StatementRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Estado | 'all'>('pendiente')
  const [pendingId, setPendingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Traemos TODAS las solicitudes (limite 200 por UX) y filtramos en
      // cliente. Asi los contadores de los chips son correctos y el cambio
      // de filtro es instantaneo sin network round-trip.
      const { data, error: err } = await supabase
        .from('statement_request')
        .select(
          'id, cuenta_id, asociado_id, canal, contacto, periodo, estado, ' +
            'requested_at, attended_at, notas_admin, ' +
            'asociado:asociado_id ( no_asociado, nombre_completo )',
        )
        .order('requested_at', { ascending: false })
        .limit(200)
      if (err) throw err
      setRows((data ?? []) as unknown as StatementRow[])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error desconocido.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const visibleRows = useMemo(() => {
    if (filter === 'all') return rows
    return rows.filter((r) => r.estado === filter)
  }, [rows, filter])

  const setEstado = async (id: string, nextEstado: Estado) => {
    setPendingId(id)
    setError(null)
    try {
      const patch: Record<string, unknown> = { estado: nextEstado }
      if (nextEstado === 'enviado' || nextEstado === 'cancelado') {
        patch.attended_at = new Date().toISOString()
        const { data: u } = await supabase.auth.getUser()
        if (u.user?.id) patch.attended_by = u.user.id
      }
      const { error: err } = await supabase
        .from('statement_request')
        .update(patch)
        .eq('id', id)
      if (err) throw err
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error desconocido.')
    } finally {
      setPendingId(null)
    }
  }

  const counts = useMemo(() => {
    const base: Record<string, number> = {
      pendiente: 0,
      en_proceso: 0,
      enviado: 0,
      cancelado: 0,
    }
    for (const r of rows) base[r.estado] = (base[r.estado] ?? 0) + 1
    return base
  }, [rows])

  return (
    <>
      <div className="filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            className={'chip' + (filter === f.id ? ' active' : '')}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            {f.id !== 'all' && counts[f.id] !== undefined ? (
              <span style={{ opacity: 0.7, fontVariant: 'tabular-nums' }}>
                · {counts[f.id]}
              </span>
            ) : null}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-ghost"
          onClick={load}
          disabled={loading}
          style={{ padding: '6px 14px' }}
        >
          {loading ? 'Recargando…' : 'Recargar'}
        </button>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Solicitada</th>
              <th>Asociado</th>
              <th>Cuenta</th>
              <th>Canal / Contacto</th>
              <th>Periodo</th>
              <th>Estado</th>
              <th className="actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty">Cargando…</div>
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty">
                    <div className="empty-title">Sin solicitudes</div>
                    <div className="empty-sub">
                      No hay registros para el filtro seleccionado.
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {formatRelative(r.requested_at)}
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {formatDateTime(r.requested_at)}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {r.asociado?.nombre_completo ?? '—'}
                    </div>
                    <div className="mono muted" style={{ fontSize: 12 }}>
                      {r.asociado?.no_asociado ?? '—'}
                    </div>
                  </td>
                  <td className="mono" style={{ fontSize: 12.5 }}>
                    {r.cuenta_id}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{CANAL_LABEL[r.canal]}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {r.contacto}
                    </div>
                  </td>
                  <td>{PERIODO_LABEL[r.periodo] ?? r.periodo}</td>
                  <td>
                    <span className={`badge badge-${r.estado}`}>
                      {ESTADO_LABEL[r.estado] ?? r.estado}
                    </span>
                    {r.attended_at ? (
                      <div
                        className="muted"
                        style={{ fontSize: 11, marginTop: 4 }}
                      >
                        {formatRelative(r.attended_at)}
                      </div>
                    ) : null}
                  </td>
                  <td className="actions">
                    <RowActions
                      estado={r.estado}
                      pending={pendingId === r.id}
                      onTomar={() => setEstado(r.id, 'en_proceso')}
                      onMarcarEnviado={() => setEstado(r.id, 'enviado')}
                      onCancelar={() => setEstado(r.id, 'cancelado')}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

type RowActionsProps = {
  estado: Estado
  pending: boolean
  onTomar: () => void
  onMarcarEnviado: () => void
  onCancelar: () => void
}

function RowActions({
  estado,
  pending,
  onTomar,
  onMarcarEnviado,
  onCancelar,
}: RowActionsProps) {
  if (pending) return <span className="muted">…</span>

  if (estado === 'pendiente') {
    return (
      <div style={{ display: 'inline-flex', gap: 6 }}>
        <button className="btn btn-ghost" onClick={onTomar}>
          Tomar
        </button>
        <button className="btn btn-primary" onClick={onMarcarEnviado}>
          Marcar enviado
        </button>
      </div>
    )
  }
  if (estado === 'en_proceso') {
    return (
      <div style={{ display: 'inline-flex', gap: 6 }}>
        <button className="btn btn-danger" onClick={onCancelar}>
          Cancelar
        </button>
        <button className="btn btn-primary" onClick={onMarcarEnviado}>
          Marcar enviado
        </button>
      </div>
    )
  }
  return <span className="muted" style={{ fontSize: 12 }}>—</span>
}
